import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  notificationsSocketService,
  type PayoutBatchSocketPayload,
} from "@/modules/dashboard/notifications/services/notifications-socket.service";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { createAccessLatch } from "@/shared/utils/accessLatch";

import { DETAIL_POLL_MS } from "../constants/payoutBatches.constants";
import { PayoutBatchesService } from "../services/payoutBatches.service";
import type { PayoutBatchDetail } from "../types/payoutBatches.types";
import { type BatchError, classifyBatchError } from "../utils/batchErrors";
import { mergeBatchSnapshot, shouldReadAfterPush } from "../utils/batchLiveMerge";
import { isLiveStatus, isReviewStatus } from "../utils/batchStatus";

/** Session-scoped "denied" latch shared by every batch read — see `@/shared/utils/accessLatch`. */
const batchAccessLatch = createAccessLatch();

export type BatchRowUpdate = NonNullable<PayoutBatchSocketPayload["last_item"]>;
export type BatchRowUpdateListener = (row: BatchRowUpdate) => void;

interface LiveState {
  key: string;
  batch: PayoutBatchDetail | null;
  notFound: boolean;
  error: BatchError | null;
  lastSequence: number;
  progressPct: number | null;
  lastUpdatedAt: Date | null;
  /** Bumps on every full read or adopted mutation result. */
  revision: number;
}

export interface UsePayoutBatchLiveResult {
  batch: PayoutBatchDetail | null;
  /** No read settled yet for this id. */
  isLoading: boolean;
  notFound: boolean;
  error: BatchError | null;
  hasAccess: boolean;
  /** The batch can still move on its own (validating, executing) — the screen polls. */
  isLive: boolean;
  progressPct: number | null;
  lastUpdatedAt: Date | null;
  revision: number;
  /** Resolves with the batch it read, or null when the read failed or a newer one superseded it. */
  refresh: () => Promise<PayoutBatchDetail | null>;
  /** Adopts the batch a mutation answered with (selection, execute, cancel…) as the newest snapshot. */
  applyBatch: (next: PayoutBatchDetail) => void;
  /** Row-level pushes (`last_item` of the socket payload) for the items page to patch in place. */
  subscribeRowUpdates: (listener: BatchRowUpdateListener) => () => void;
}

/**
 * One batch, kept current three ways: a full `GET /:id` on open and on every refresh, a 5 s poll only while
 * the batch is live, and the `payout_batch:updated` push merged in between (status and counters only — the
 * full read always wins, and a push that says the batch left a live state triggers one so totals and the
 * summary are fresh). State is keyed by id and derived, not synchronized: another id simply stops matching.
 */
export function usePayoutBatchLive(id: string): UsePayoutBatchLiveResult {
  const { user } = useAuth();
  const organizationId = user?.organization_id ?? user?.organization?.id ?? null;
  const deniedKey = `${user?.id ?? "anon"}:${organizationId ?? "no-org"}`;
  const hasAccess = !batchAccessLatch.useDenied(deniedKey);

  const [state, setState] = useState<LiveState | null>(null);
  const current = state?.key === id ? state : null;
  const requestSeq = useRef(0);
  const rowListeners = useRef(new Set<BatchRowUpdateListener>());

  const read = useCallback(async (): Promise<PayoutBatchDetail | null> => {
    if (!id || batchAccessLatch.isDenied(deniedKey)) return null;
    const seq = ++requestSeq.current;
    try {
      const { data } = await PayoutBatchesService.getOne(id);
      if (seq !== requestSeq.current) return null;
      setState((prev) => {
        const same = prev?.key === id ? prev : null;
        return {
          key: id,
          batch: data,
          notFound: false,
          error: null,
          lastSequence: same?.lastSequence ?? 0,
          progressPct: same?.progressPct ?? null,
          lastUpdatedAt: new Date(),
          revision: (same?.revision ?? 0) + 1,
        };
      });
      return data;
    } catch (err: unknown) {
      if (seq !== requestSeq.current) return null;
      if (HttpApiService.isAccessDeniedError(err)) {
        batchAccessLatch.latch(deniedKey);
        return null;
      }
      const classified = classifyBatchError(err);
      setState((prev) => {
        const same = prev?.key === id ? prev : null;
        return {
          key: id,
          batch: same?.batch ?? null,
          notFound: classified.kind === "not_found",
          error: classified,
          lastSequence: same?.lastSequence ?? 0,
          progressPct: same?.progressPct ?? null,
          lastUpdatedAt: same?.lastUpdatedAt ?? null,
          revision: same?.revision ?? 0,
        };
      });
      return null;
    }
  }, [deniedKey, id]);

  const applyBatch = useCallback(
    (next: PayoutBatchDetail) => {
      // A read already in flight predates this answer; letting it land would put older totals back on screen.
      requestSeq.current += 1;
      setState((prev) => {
        const same = prev?.key === id ? prev : null;
        return {
          key: id,
          batch: next,
          notFound: false,
          error: null,
          lastSequence: same?.lastSequence ?? 0,
          progressPct: same?.progressPct ?? null,
          lastUpdatedAt: new Date(),
          revision: (same?.revision ?? 0) + 1,
        };
      });
    },
    [id],
  );

  const readRef = useRef(read);
  const heldRef = useRef<PayoutBatchDetail | null>(null);
  useEffect(() => {
    readRef.current = read;
    heldRef.current = current?.batch ?? null;
  });

  // Through the ref on purpose: the read settles asynchronously, so no render cascades from here.
  useEffect(() => {
    void readRef.current();
  }, [id, deniedKey]);

  const status = current?.batch?.status ?? null;
  const isLive = status !== null && isLiveStatus(status);

  useEffect(() => {
    if (!isLive || !hasAccess) return;
    const interval = setInterval(() => void read(), DETAIL_POLL_MS);
    return () => clearInterval(interval);
  }, [isLive, hasAccess, read]);

  // A batch under review or waiting for approval only moves by a person's hand, so it does not poll; coming back
  // to the tab re-reads it once instead, in case a socket push was missed.
  useEffect(() => {
    if (!hasAccess || status === null || !isReviewStatus(status)) return;
    const onFocus = () => void read();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [hasAccess, status, read]);

  useEffect(() => {
    if (!hasAccess) return;
    let pending: PayoutBatchSocketPayload | null = null;
    let frame: number | null = null;

    // One merge per frame: a 1000-row run is ~1000 pushes, and each one is a render.
    const flush = () => {
      frame = null;
      const payload = pending;
      pending = null;
      if (!payload) return;
      setState((prev) => {
        if (!prev || prev.key !== id || !prev.batch) return prev;
        const merged = mergeBatchSnapshot(prev.batch, payload, prev.lastSequence);
        if (!merged.accepted) return prev;
        return {
          ...prev,
          batch: merged.next,
          lastSequence: merged.lastSequence,
          progressPct: merged.progressPct ?? prev.progressPct,
          lastUpdatedAt: new Date(),
        };
      });
    };

    const off = notificationsSocketService.onPayoutBatchUpdate((payload) => {
      if (payload.id !== id) return;
      pending = payload;
      if (frame === null) frame = window.requestAnimationFrame(flush);
      if (payload.last_item) {
        const row = payload.last_item;
        rowListeners.current.forEach((listener) => listener(row));
      }
      if (shouldReadAfterPush(heldRef.current, payload)) void readRef.current();
    });

    return () => {
      off();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [hasAccess, id]);

  const subscribeRowUpdates = useCallback((listener: BatchRowUpdateListener) => {
    rowListeners.current.add(listener);
    return () => {
      rowListeners.current.delete(listener);
    };
  }, []);

  return {
    batch: current?.batch ?? null,
    isLoading: hasAccess && current === null,
    notFound: current?.notFound ?? false,
    error: current?.error ?? null,
    hasAccess,
    isLive,
    progressPct: current?.progressPct ?? null,
    lastUpdatedAt: current?.lastUpdatedAt ?? null,
    revision: current?.revision ?? 0,
    refresh: read,
    applyBatch,
    subscribeRowUpdates,
  };
}
